import {
  addError,
  addLoading,
  getError,
  getLoading,
  isSupersededRequest,
  removeError,
  removeLoadingAndError,
} from '../core/redux/api/module';
import { breakerHeldUntil, hasTrippedBreaker, updateBreaker } from '../core/redux/api/circuitBreaker/module';
import { apiGetResortMappingErrors } from '../core/redux/api/endpoints';
import { IAppState } from '../core/redux/IAppState';
import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
} from '../core/redux/api/reduxStandardApiAction';
import { ACTION_TYPE } from '../core/redux/actionType';
import { Action } from 'redux';
import { assoc, isNilOrEmpty } from '../language/module';
import {ResortMappingObject, RTPMappingProduct, SiriuswareMappingProduct} from './productMappings';
import { getResource } from '../language/resources/getResource';
import { v4 as uuidv4 } from 'uuid';
import { getProductHydrationErrors } from './hydrationErrors';
import * as R from 'ramda';
import { ApiError } from '../core/redux/api/apiError';


const KEY_ACTION = ACTION_TYPE.RESORT_MAPPING_ERROR_REQUEST;
export const ACTIONS    = [
  ACTION_TYPE.RESORT_MAPPING_ERROR_REQUEST,
  ACTION_TYPE.RESORT_MAPPING_ERROR_RESPONSE,
  ACTION_TYPE.RESORT_MAPPING_ERROR_ERROR,
];


/**
 * Whether a mapping-error load is in flight for the given resort. This request is keyed by resort
 * abbreviation, not by action type, so read it through here rather than calling getLoading at the component.
 */
export const isResortMappingErrorLoading = (state: IAppState, resortAbbreviation: string): boolean =>
  !!getLoading(state, resortAbbreviation, KEY_ACTION);


/**
 * Whether the breaker is holding this request open, so dispatching the action would bail out without
 * firing. A caller that waits on the load has to check this first or it waits forever.
 */
export const isResortMappingErrorSuppressed = (state: IAppState): boolean =>
  hasTrippedBreaker(state, KEY_ACTION);


/** The last moment the breaker holds this request, once it has tripped. Compare it with the clock. */
export const getResortMappingErrorHeldUntil = (state: IAppState): number | undefined =>
  breakerHeldUntil(state, KEY_ACTION);


/** Whether mapping errors for the given resort have landed since the last switch. */
export const haveResortMappingErrorsLoaded = (state: IAppState, resortAbbreviation: string): boolean =>
  state.mappingErrorsLoadedFor === resortAbbreviation;


/** The given resort's last mapping-error load failure. Kept until the next request or a switch. */
export const getResortMappingErrorLoadError = (state: IAppState, resortAbbreviation: string): ApiError | undefined =>
  getError(state, resortAbbreviation, KEY_ACTION);


/** Whether that failure was a missing Sales Channel Code: the one cause a user can act on, so it gets its own message. */
export const isResortMappingErrorMissingSalesChannel = (state: IAppState, resortAbbreviation: string): boolean =>
  !!getResortMappingErrorLoadError(state, resortAbbreviation)?.extras?.missingSalesChannel;


/**
 * Dispatch immediately before getResortMappingErrorAction. The switch and the page both ask, in the same
 * commit once a config lands, and bailout cannot dedupe that: the middleware checks it synchronously but
 * only registers the request as loading a microtask later.
 */
export const resortMappingErrorRequestedAction = (resortAbbreviation: string) => ({
  type: ACTION_TYPE.RESORT_MAPPING_ERROR_REQUESTED,
  resortAbbreviation,
});


/**
 * Marks Mapping Status as not yet requested, so the page's next visit loads it again instead of reusing statuses loaded
 * in the last 5 minutes.
 */
export const resortMappingErrorsOutOfDateAction = () => resortMappingErrorRequestedAction('');


/** Asked for, with something to show for it. Cleared on a resort switch and on a load that brought nothing. */
export const hasRequestedResortMappingErrors = (state: IAppState, resortAbbreviation: string): boolean =>
  state.mappingErrorsRequestedFor === resortAbbreviation;


/** How long mapping errors loaded ahead of time stay worth showing. */
export const MAPPING_ERRORS_MAX_AGE_MS = 5 * 60 * 1000;


/**
 * A load that has not landed yet is not stale — the ask is what holds off the other caller, and treating
 * it as stale would let that caller through. A time stamped for another resort says nothing about this one,
 * so read it through here rather than off state: nothing then rests on a switch having cleared it.
 */
export const areResortMappingErrorsStale = (
  state: IAppState,
  resortAbbreviation: string,
  maxAgeMs: number = MAPPING_ERRORS_MAX_AGE_MS,
): boolean => state.mappingErrorsLoadedFor === resortAbbreviation
  && state.mappingErrorsLoadedAt != null
  && Date.now() - state.mappingErrorsLoadedAt > maxAgeMs;


/**
 * The one rule both the resort switch and the page ask by. A suppressed dispatch never fires and so never
 * lands, and the ask is recorded before dispatching — recording one here would outlive the cool-down that
 * suppressed it and hold off every later caller.
 */
export const shouldLoadResortMappingErrors = (state: IAppState, resortAbbreviation: string): boolean =>
  !isResortMappingErrorSuppressed(state)
  && (!hasRequestedResortMappingErrors(state, resortAbbreviation)
    || areResortMappingErrorsStale(state, resortAbbreviation));


/** Only the resort whose ask is on record can take it off again. */
const forgetRequestFor = (state: IAppState, resortAbbreviation: string): IAppState =>
  hasRequestedResortMappingErrors(state, resortAbbreviation)
    ? assoc('mappingErrorsRequestedFor', '', state)
    : state;


const RTP_EDITABLE_FIELDS: (keyof RTPMappingProduct)[] = ['productHeaderCode', 'accessRule', 'mediaProductHeaderCode'];
const SW_EDITABLE_FIELDS: (keyof SiriuswareMappingProduct)[] = ['department', 'category', 'item'];


/**
 * Lays the checked statuses over rows on screen, keeping their values. A status checked against values a row no longer
 * has stays out, unless the row has none yet. Every row is a new object, since the caller writes hydration errors onto
 * them.
 */
const withStatusesFrom = <T extends RTPMappingProduct | SiriuswareMappingProduct>(
  onScreen: T[],
  checked: T[],
  editableFields: (keyof T)[],
): T[] => {
  const checkedById = new Map(checked.map(row => [row.id, row]));
  return onScreen.map(row => {
    const check = checkedById.get(row.id);
    const sameValues = !!check && editableFields.every(field => row[field] === check[field]);
    return check && (sameValues || row.isError === null)
      ? { ...row, isError: check.isError, errorDescription: check.errorDescription }
      : { ...row };
  });
};


export const resortMappingErrorRequestedReducer = (state: IAppState, action: Action): IAppState =>
  action.type === ACTION_TYPE.RESORT_MAPPING_ERROR_REQUESTED
    ? assoc('mappingErrorsRequestedFor', (action as ReturnType<typeof resortMappingErrorRequestedAction>).resortAbbreviation, state)
    : state;


/**
 * Makes an action that can be dispatched to get resort product mappings with product mapping errors
 */
export const getResortMappingErrorAction = (
  resortAbbreviation: string,
) => {
  const requestId = uuidv4();
  // A save landing after this means the response may hold the values from before it
  const requestedAt = Date.now();

  return makeApiAction(
    {
      method:   'GET',
      headers:  (state: IAppState) => ({
        'content-type':  'application/json',
        'Authorization': `Bearer ${state.auth.bearerToken}`,
      }),
      endpoint: apiGetResortMappingErrors(resortAbbreviation),
      types:     [
        {
          type: ACTIONS[0],
          meta: { requestId, resortAbbreviation, requestedAt },
        },
        { 
          type: ACTIONS[1],
          meta: { requestId, resortAbbreviation, requestedAt },
        },
        {
          type: ACTIONS[2],
          meta: { requestId, resortAbbreviation, requestedAt },
        }
      ],
      // Bail if already loading results for that query for that resort
      bailout: (state: IAppState) => isResortMappingErrorSuppressed(state) || isResortMappingErrorLoading(state, resortAbbreviation),
    },
  );
}


const reducer: ApiReducer<ResortMappingObject> = (
    state,
    action,
    error,
    isLoading,
    payload
): IAppState => {
  const resort         = action.meta.resortAbbreviation;
  const appStateResort = R.path<string>(['selectedResort', 'resortAbbreviation'], state);

  // Ignore the results if the response is for a previous query that didn't complete before the user switched resorts 
  if (action.type === ACTION_TYPE.RESORT_MAPPING_ERROR_RESPONSE && !isNilOrEmpty(appStateResort) && appStateResort !== resort) {
    const withBreaker = updateBreaker(state, KEY_ACTION, !!error);
    return removeLoadingAndError(withBreaker, resort, KEY_ACTION);
  }

  if  (action.type === KEY_ACTION) {
    const newState = assoc('mappingErrorRequestId', action.meta.requestId, state);
    // Names its owner, so an earlier request landing late cannot settle it
    return addLoading(newState, resort, KEY_ACTION, { requestId: action.meta.requestId });
  }

  if (isLoading) {
    return addLoading(state, resort, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  // Superseded: a later request for this resort owns its loading entry, error and rows. The ask goes all the
  // same — a duplicate costs a GET, a latched one the Mapping Status column until the next resort switch.
  if (isSupersededRequest(state, resort, KEY_ACTION, action.meta.requestId, state.mappingErrorRequestId)) {
    return forgetRequestFor(withBreaker, resort);
  }

  if (error) {
    // A failed ask has nothing to show, so it must not count as asked or the next mount will not retry.
    // Only this resort's though: the failure of a load left in flight by a switch would otherwise take
    // the record of the ask made for the resort now selected with it.
    const notRequested = forgetRequestFor(withBreaker, resort);

    // Only a JSON failure has a response: the gateway's plain-text 504 and a dropped connection do not
    if (action.payload?.response?.detail?.includes('Sales Channel Code')) {
      return addError(
        notRequested,
        resort,
        KEY_ACTION,
        getResource(state.locale, 'PRODUCT_MAPPING_PAGE_MISSING_SALES_CHANNEL'),
        { missingSalesChannel: true },
      );
    }
    return addError(
      notRequested,
      resort,
      KEY_ACTION,
      getResource(state.locale, 'GET_RESORT_MAPPING_ACTION_ERROR'),
    );
  }

  // Remove loading/error since we're not loading or erroring
  const noLoadingOrError = removeLoadingAndError(withBreaker, resort, KEY_ACTION);

  // A load that landed with nothing to show must not count as asked either, or the next mount will not retry
  const nothingLoaded = forgetRequestFor(noLoadingOrError, resort);

  // No rows means no statuses either. Without an error stored, the column would read loading for good.
  if (!payload?.productMappings) {
    return addError(
      nothingLoaded,
      resort,
      KEY_ACTION,
      getResource(state.locale, 'GET_RESORT_MAPPING_ACTION_ERROR'),
    );
  }

  const resortMappingObject = payload;

  // These rows stand in for the mappings load's, so a failure of that load has nothing left to report
  const rowsSupplied = removeError(noLoadingOrError, resort, ACTION_TYPE.RESORT_MAPPING_REQUEST);

  // Stamped only where rows are written, so the age always belongs to what is on screen, and stamped with
  // the resort it belongs to so no later reader has to assume whose age it is
  const loaded = assoc('mappingErrorsLoadedFor', resort,
    assoc('mappingErrorsLoadedAt', Date.now(), rowsSupplied));

  // A save that landed once this request was out may postdate the values it holds. Same millisecond counts.
  const savedSinceRequest = state.productMappingsSavedAt !== undefined
    && state.productMappingsSavedAt >= action.meta.requestedAt;

  // The rows on screen while they hold unsaved edits, a save in flight or one newer than this response, which it must
  // not replace. Otherwise it does, so a Sync shows values changed elsewhere.
  const onScreen = (state.productMappingHasUnsavedEdits || savedSinceRequest)
    && state.resortMappingHistories.posProvider === resortMappingObject.posProvider
    ? state.resortMappingHistories.productMappings
    : [];
    /**
     * Typescript still doesn't support .filter on < T[] | O[] > so for now we need to type assert based on POS Provider
     */
  if (resortMappingObject.posProvider === 'RTP') {
    const rtpMappings = resortMappingObject.productMappings as RTPMappingProduct[];
    const filteredMappings = rtpMappings.filter(mapping => mapping.isResortConfigurable);
    const rows = onScreen.length > 0
      ? withStatusesFrom(onScreen as RTPMappingProduct[], filteredMappings, RTP_EDITABLE_FIELDS)
      : filteredMappings;
    const mappingsWithHydrationErrors = rows.map((mapping) => {
      mapping.hydrationErrors = getProductHydrationErrors(state.productHydrationErrors, mapping.entitlementTypeId, mapping.fulfillmentSeasonId, mapping.ageBin);
      return mapping;
    });

    return assoc(
      'resortMappingHistories',
      {
        posProvider: payload.posProvider,
        productMappings: mappingsWithHydrationErrors,
        hasError: payload.hasError
      },
      loaded
    );
  } else {
    const siriuswareMappings = resortMappingObject.productMappings as SiriuswareMappingProduct[];
    const filteredMappings = siriuswareMappings.filter(mapping => mapping.isResortConfigurable);
    const rows = onScreen.length > 0
      ? withStatusesFrom(onScreen as SiriuswareMappingProduct[], filteredMappings, SW_EDITABLE_FIELDS)
      : filteredMappings;
    const mappingsWithHydrationErrors = rows.map((mapping) => {
      mapping.hydrationErrors = getProductHydrationErrors(state.productHydrationErrors, mapping.entitlementTypeId, mapping.fulfillmentSeasonId, mapping.ageBin);
      return mapping;
  });

    return assoc(
      'resortMappingHistories',
      {
        posProvider: payload.posProvider,
        productMappings: mappingsWithHydrationErrors,
        hasError: payload.hasError
      },
      loaded
    );
  }

};


/**
 * Function that maps actions into new app state. Specifically, updates app state with information from actions related
 * to a guest's redemptions at resorts while using a specific pass. Returns a new app state, of course.
 */
export const getResortMappingErrorReducer = makeApiReducer<ResortMappingObject>(ACTIONS, KEY_ACTION, reducer);
